import { useState } from 'react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

export default function ProfitLoss() {
  const [dateRange, setDateRange] = useState({
    start: '2026-01-01',
    end: '2026-01-31',
  })

  // Demo data
  const report = {
    revenue: {
      foodSales: 42350,
      beverageSales: 12680,
      deliveryRevenue: 8750,
      cateringRevenue: 2500,
      total: 66280,
    },
    cogs: {
      foodCost: 14823,
      beverageCost: 3170,
      total: 17993,
    },
    grossProfit: 48287,
    expenses: {
      wagesSalaries: 15670,
      rent: 4500,
      utilities: 1250,
      deliveryFees: 2625,
      processingFees: 1924,
      supplies: 890,
      marketing: 650,
      other: 420,
      total: 27929,
    },
    netIncome: 20358,
  }

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profit & Loss Statement</h1>
          <p className="text-gray-500 mt-1">Financial performance overview</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="input py-1.5"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="input py-1.5"
            />
          </div>
          <Button variant="secondary">Export PDF</Button>
        </div>
      </div>

      {/* Report */}
      <Card className="max-w-3xl">
        <div className="text-center mb-8 pb-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Profit & Loss Statement</h2>
          <p className="text-gray-500 mt-1">
            For the period {dateRange.start} to {dateRange.end}
          </p>
        </div>

        <div className="space-y-8">
          {/* Revenue */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Revenue
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Food Sales</span>
                <span className="font-medium">{formatCurrency(report.revenue.foodSales)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Beverage Sales</span>
                <span className="font-medium">{formatCurrency(report.revenue.beverageSales)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Delivery Revenue</span>
                <span className="font-medium">{formatCurrency(report.revenue.deliveryRevenue)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Catering Revenue</span>
                <span className="font-medium">{formatCurrency(report.revenue.cateringRevenue)}</span>
              </div>
              <div className="flex justify-between py-3 border-t border-gray-200 font-semibold">
                <span>Total Revenue</span>
                <span className="text-green-600">{formatCurrency(report.revenue.total)}</span>
              </div>
            </div>
          </section>

          {/* Cost of Goods Sold */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Cost of Goods Sold
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Food Cost</span>
                <span className="font-medium">{formatCurrency(report.cogs.foodCost)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Beverage Cost</span>
                <span className="font-medium">{formatCurrency(report.cogs.beverageCost)}</span>
              </div>
              <div className="flex justify-between py-3 border-t border-gray-200 font-semibold">
                <span>Total COGS</span>
                <span>{formatCurrency(report.cogs.total)}</span>
              </div>
            </div>
          </section>

          {/* Gross Profit */}
          <div className="flex justify-between py-4 bg-gray-50 -mx-6 px-6 font-bold text-lg">
            <span>Gross Profit</span>
            <span className="text-green-600">{formatCurrency(report.grossProfit)}</span>
          </div>

          {/* Operating Expenses */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Operating Expenses
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Wages & Salaries</span>
                <span className="font-medium">{formatCurrency(report.expenses.wagesSalaries)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Rent</span>
                <span className="font-medium">{formatCurrency(report.expenses.rent)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Utilities</span>
                <span className="font-medium">{formatCurrency(report.expenses.utilities)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Delivery Platform Fees</span>
                <span className="font-medium">{formatCurrency(report.expenses.deliveryFees)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Credit Card Processing</span>
                <span className="font-medium">{formatCurrency(report.expenses.processingFees)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Supplies</span>
                <span className="font-medium">{formatCurrency(report.expenses.supplies)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Marketing</span>
                <span className="font-medium">{formatCurrency(report.expenses.marketing)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Other</span>
                <span className="font-medium">{formatCurrency(report.expenses.other)}</span>
              </div>
              <div className="flex justify-between py-3 border-t border-gray-200 font-semibold">
                <span>Total Expenses</span>
                <span>{formatCurrency(report.expenses.total)}</span>
              </div>
            </div>
          </section>

          {/* Net Income */}
          <div className="flex justify-between py-4 bg-primary-50 -mx-6 px-6 font-bold text-xl rounded-b-lg">
            <span>Net Income</span>
            <span className="text-primary-700">{formatCurrency(report.netIncome)}</span>
          </div>
        </div>
      </Card>
    </div>
  )
}
